import * as React from "react";
import clsx from "clsx";
import { useT } from "../../../../../app/lib/i18n";
import * as s from "../../FormInput.style.css";
import { validatePhone } from "../../logic/phoneValidation";
import type { PhoneInputProps } from "./PhoneInput.types";

export const PhoneInput = React.forwardRef<HTMLDivElement, PhoneInputProps>(
  function PhoneInput(
    {
      label,
      countryCode,
      phone,
      onCountryCodeChange,
      onPhoneChange,
      countryCodePlaceholder = "1",
      phonePlaceholder = "5551234567",
      disabled,
      hint,
      error: controlledError,
      id: providedId,
    },
    ref
  ) {
    const t = useT();
    const id = providedId ?? React.useId();
    const validationError = disabled ? undefined : validatePhone(countryCode, phone);
    const error = controlledError ?? validationError;
    const hintId = hint ? `${id}-hint` : undefined;

    return (
      <div ref={ref} className={s.wrapper}>
        {label && (
          <label htmlFor={`${id}-phone`} className={s.label}>
            {label}
          </label>
        )}
        <div className={s.phoneRow}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="tel-country-code"
            id={`${id}-country`}
            value={countryCode}
            onChange={(e) => onCountryCodeChange(e.target.value)}
            placeholder={countryCodePlaceholder}
            disabled={disabled}
            className={clsx(s.countryCodeInput, error && s.inputError)}
            aria-label={t("shared.form.countryCode")}
            aria-invalid={!!error}
          />
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            id={`${id}-phone`}
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder={phonePlaceholder}
            disabled={disabled}
            className={clsx(s.phoneInput, error && s.inputError)}
            aria-label={t("shared.form.phoneNumber")}
            aria-invalid={!!error}
            aria-describedby={[error ? `${id}-error` : undefined, hintId].filter(Boolean).join(" ") || undefined}
          />
        </div>
        {error && (
          <span id={`${id}-error`} className={s.errorText} role="alert">
            {error}
          </span>
        )}
        {hint && !error && (
          <span id={hintId} className={s.hintText}>
            {hint}
          </span>
        )}
      </div>
    );
  }
);
